import { graphHooks } from '@tunasong/graph-lib/react'
import { logger } from '@tunasong/models'
import type { Availability, ManyOfManyPoll, ManyOfManyPollResponse, Persisted } from '@tunasong/schemas'
import { useNavigateToEntity } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useRef, useState } from 'react'
import { toChoices } from './availability.js'
import { DateSlotList } from './date-slot-list.js'
import { buildMemberNames } from './member-names.js'

export interface DatePollVoteProps {
  poll: Persisted<ManyOfManyPoll>
  userId: string
}

/** What one answer writes: the three-state value plus the binary projection, never one alone. */
type AnswerPatch = Pick<ManyOfManyPollResponse, 'availability' | 'choices'>

/**
 * Container for the respondent surface. Every answer auto-saves and stays editable until the
 * poll closes — there is no Save button, which is why `getWaitingOn` needs its own definition
 * of "done".
 *
 * That same absence is why this component owns its answers rather than reading them back from
 * `useEntityChildren`. A respondent working down a ten-slot poll answers again long before the
 * previous write has round-tripped, so the query result is a lagging read and must be treated
 * as a seed, never as the truth about what this user has said.
 */
export const DatePollVote: FC<DatePollVoteProps> = props => {
  const { poll, userId } = props

  const { entities: responses = [], isSuccess } = graphHooks.useEntityChildren<ManyOfManyPollResponse>({
    parentId: poll.id,
    filter: 'pollresponse',
  })
  const { members } = graphHooks.useMembers(poll)
  const { createEntity } = graphHooks.useEntityGraph()
  const updateEntity = graphHooks.useEntityUpdate<ManyOfManyPollResponse>({ debounceDelay: 500 })

  const myResponse = responses.find(response => response.userId === userId)

  /**
   * Answers made in this session, laid over the server's copy.
   *
   * Deriving the next payload from the query alone means the second of two quick answers
   * carries the first's PRE-answer value and silently reverts it — the user watches their own
   * answer flip back. Only this user writes this response, so a local answer is always the
   * newer of the two for the slot it covers.
   */
  const [localAnswers, setLocalAnswers] = useState<Record<string, Availability>>({})
  const myAvailability = { ...(myResponse?.availability ?? {}), ...localAnswers }

  /**
   * The one create for this respondent, in flight or settled, resolving to the response id.
   *
   * `myResponse` cannot be the guard against a second create: it comes from the same lagging
   * read, so every answer given before the refetch lands takes the create path and writes
   * ANOTHER `pollresponse` for the same user. Duplicates inflate the `getProgress` denominator,
   * so the tally goes quietly wrong for everyone rather than erroring — the same failure
   * `one-of-many/vote.tsx` already fixed once, re-entering through the auto-save door.
   *
   * It resolves to `undefined` instead of rejecting: later answers await it, and a floated
   * await on a rejected promise is an unhandled rejection.
   */
  const creation = useRef<Promise<string | undefined> | null>(null)

  const navigateToEntity = useNavigateToEntity()
  const isOwner = poll.userId === userId

  const createResponse = async (patch: AnswerPatch) => {
    const entity = {
      type: 'pollresponse',
      pollType: 'many-of-many',
      ...patch,
    } satisfies ManyOfManyPollResponse

    try {
      const created = await createEntity({ parent: poll, entity }).unwrap()
      return created.id
    } catch (error) {
      /** Let the next answer try again rather than latching the respondent out of the poll. */
      creation.current = null
      logger.warn('Failed to create the poll response', error)
      return undefined
    }
  }

  const handleAnswer = (idx: number, value: Availability) => {
    const availability = { ...myAvailability, [String(idx)]: value }
    setLocalAnswers(current => ({ ...current, [String(idx)]: value }))

    /** `choices` is the projection every pre-existing consumer reads. Written together, always. */
    const patch: AnswerPatch = { availability, choices: toChoices(availability) }

    if (myResponse) {
      updateEntity(myResponse.id, patch)
      return
    }

    /** Assigned before anything can await, so two answers in one tick cannot both create. */
    if (!creation.current) {
      creation.current = createResponse(patch)
      return
    }

    /** Answered again while the create was still out: update whatever it produced. */
    void creation.current.then(id => {
      if (id) {
        updateEntity(id, patch)
      }
    })
  }

  return (
    <DateSlotList
      poll={poll}
      responses={responses}
      myAvailability={myAvailability}
      memberIds={members.map(member => member.userId)}
      memberNames={buildMemberNames(members, userId)}
      isLoading={!isSuccess}
      readOnly={Boolean(poll.closed)}
      /** The shortfall notice's way out is "add more dates", which is the Edit layout. */
      onSuggestMore={isOwner ? () => navigateToEntity(poll, { layout: 'Edit' }) : undefined}
      onAnswer={handleAnswer}
    />
  )
}

export default DatePollVote
